import { api } from '../../lib/apiClient';
import { uploadDirect } from '../../lib/directUpload';
import { useAuthStore } from '../../store/authStore';

export const secretKeys = {
  list: (params) => ['secrets', params ?? {}],
  detail: (id) => ['secret-detail', id],
  meta: (token) => ['secret-meta', token],
};

function ws() {
  const workspaceId = useAuthStore.getState().workspaceId;
  if (!workspaceId) throw new Error('No active workspace selected');
  return `/workspaces/${workspaceId}`;
}

/** Build the public reveal link a sender copies/shares. */
export function messageLink(token) {
  return `${window.location.origin}/m/${token}`;
}

/**
 * Create an encrypted message (+ optional attachments). Returns the reveal
 * token ONCE. Each attachment is first encrypted on this device and uploaded
 * straight to storage; the message then references the finished uploads.
 * `onProgress(percent)` covers all attachments; `onFileProgress(index, percent)` each one.
 */
export async function createSecret({
  name,
  value,
  password,
  expiresInSeconds,
  maxViews,
  oneTime,
  recipientEmail,
  files = [],
  onProgress,
  onFileProgress,
  signal,
}) {
  const totalBytes = files.reduce((sum, f) => sum + f.size, 0);
  let doneBytes = 0;
  const attachmentUploadTokens = [];
  for (const [index, file] of files.entries()) {
    let uploadToken;
    await uploadDirect({
      file,
      signal,
      begin: async () => {
        const { data } = await api.post(`${ws()}/secrets/attachments/uploads`, {
          name: file.name,
          size: file.size,
          mimeType: file.type || undefined,
        });
        uploadToken = data.uploadToken;
        return data;
      },
      onProgress: (percent) => {
        onFileProgress?.(index, percent);
        const sent = doneBytes + (file.size * percent) / 100;
        onProgress?.(Math.floor((sent / totalBytes) * 100));
      },
    });
    doneBytes += file.size;
    attachmentUploadTokens.push(uploadToken);
  }

  const { data } = await api.post(`${ws()}/secrets`, {
    name: name || 'Encrypted message',
    value,
    password: password || undefined,
    expiresInSeconds: expiresInSeconds || undefined,
    maxViews: maxViews || undefined,
    oneTime: oneTime || undefined,
    recipientEmail: recipientEmail || undefined,
    attachmentUploadTokens: attachmentUploadTokens.length ? attachmentUploadTokens : undefined,
  });
  return data; // { token, attachmentCount, id, name, ... }
}

export async function listSecrets(params = {}) {
  const { data } = await api.get(`${ws()}/secrets`, { params });
  return data;
}

/** Sender-side detail: status, attachment metadata, access log — never the plaintext. */
export async function getSecret(id) {
  const { data } = await api.get(`${ws()}/secrets/${id}`);
  return data;
}

export const revokeSecret = (id) => api.delete(`${ws()}/secrets/${id}`);

// ---- Public (recipient) ----

export async function resolveSecretMeta(token) {
  const { data } = await api.get(`/secrets/public/${token}`, { skipAuth: true });
  return data; // { name, contentType, requiresPassword, expiresAt, attachmentCount }
}

export async function revealSecret(token, password) {
  const { data } = await api.post(`/secrets/public/${token}`, password ? { password } : {}, {
    skipAuth: true,
  });
  return data; // { name, value, attachments: [...] }
}

/** Download + save a decrypted attachment (streamed through the public endpoint). */
export async function downloadSecretAttachment(token, attachment) {
  const { data } = await api.get(`/secrets/public/${token}/attachments/${attachment.id}`, {
    skipAuth: true,
    responseType: 'blob',
  });
  const url = URL.createObjectURL(data);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = attachment.name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
